/**
 * Translates the window.location.href into an argument object containing the active sport and page
 *
 * @internals app/components/UrlArgsProvider
 */
import _ from 'lodash';
import MessageBusTopics from '../../common/MessageBusTopics';
import Translator from './Translator';

/**
 * The current page arguments
 *
 * @type {Object}
 *
 * @private
 */
var args = {
    sport: '',
    page: '',
};

/**
 * Global PubSub reference
 *
 * @type {Object}
 *
 * @private
 */
var pubSub = window.WH.messageBus;

/**
 * Regular expressions for matching pages from URLs.
 *
 * @type {Array}
 *
 * @private
 */
var pageRegexes = [{
    page: 'event',
    regex: /\/OB_EV/,
    sportAtPos: 3,
    navSelector: '.navHighlights',
}, {
    page: 'matches',
    regex: /\/matches/,
    sportAtPos: 3,
    navSelector: '.navMatches',
}, {
    page: 'competitions',
    regex: /\/competitions/,
    sportAtPos: 3,
    navSelector: '.navCompetitions',
}, {
    page: 'meetings',
    regex: /\/meetings/,
    sportAtPos: 3,
    navSelector: '.navMeetings',
}, {
    page: 'antePost',
    regex: /\/future-races/,
    sportAtPos: 3,
    navSelector: '.navAntePost',
}, {
    page: 'specials',
    regex: /\/specials/,
    sportAtPos: 3,
    navSelector: '.navSpecials',
}, {
    page: 'inPlay',
    regex: /\/in-play/,
    sportAtPos: 3,
    navSelector: '.navInplay',
}, {
    page: 'whtv',
    regex: /\/schedule/,
    sportAtPos: 5,
    navSelector: '.navWHTV',
}, {
    page: 'lotteries',
    regex: /\/lotteries/,
    sportAtPos: 4,
    navSelector: '.navLotteries',
}, {
    page: 'results',
    regex: /\/results/,
    sportAtPos: 5,
    navSelector: '.navResults',
}, {
    page: 'virtual',
    regex: /\/virtual/,
    sportAtPos: 4,
    navSelector: '.navVirtual',
}, {
    page: 'coupons',
    regex: /\/coupons/,
    sportAtPos: 3,
    navSelector: '.navCoupons',
}, {
    page: 'top-bets',
    regex: /\/top-bets/,
    sportAtPos: 3,
    navSelector: '.navTopBets',
}, {
    page: 'suggestions',
    regex: /\/suggestions/,
    sportAtPos: 4,
    navSelector: '.navSuggestions',
}, {
    page: 'promotions',
    regex: /\/promotions/,
    sportAtPos: 4,
    navSelector: '.navPromotions',
}];

/**
 * Sets the order of topics to send so if both sport and page have changed only the sport topic will be emmited
 *
 * @type {Array}
 *
 * @private
 */
var publishPriority = [
    'sport',
    'page',
];

/**
 * Gets the arguments for a given pathname
 *
 * @param {string} pathname
 *
 * @returns {Object} page arguments object
 *
 * @private
 */
var getArgsFromPath = function getArgsFromPath(pathname) {

    if (isHomePage(pathname)) {

        return {
            sport: 'homepage',
            page: 'home',
            navSelector: '.navHomepage',
        };
    }

    if (isVirtual(pathname)) {
        const pathComponents = pathname.split('/');
        return {
            sport: 'virtual-world',
            page: pathComponents[pathComponents.length - 2],
            navSelector: '.navVirtual',
        };
    }

    return pageRegexes.reduce(reducePages.bind(this, pathname), {
        page: 'highlights',
        sport: getSportAtPosition(pathname, 3),
        navSelector: '.navHighlights',
    });
};

/**
 * Checks if the selected page is the homepage
 *
 * @param {string} pathname
 *
 * @returns {boolean}
 *
 * @private
 */
var isHomePage = function isHomePage(pathname) {

    return pathname.split('/').length === 3;
};

/**
 * Returns true if the path name contains apps/virtual, false otherwise
 *
 * @param pathname
 * @returns {Number|number|
    *
 * @returns {boolean}
 *
 * @private
 */
var isVirtual = function isVirtual(pathname) {
    return /apps\/virtual/.test(pathname) || /virtual-world/.test(pathname);
};

/**
 * Reduce callback for figuring out the current page.
 *
 * @param {string} pathname
 * @param {String} prev
 * @param {Object} current
 *
 * @returns {String}
 *
 * @private
 */
var reducePages = function reducePages(pathname, prev, current) {

    if (current.regex.test(pathname)) {

        return {
            page: current.page,
            sport: getSportAtPosition(pathname, current.sportAtPos),
            navSelector: current.navSelector,
        };
    }

    return prev;
};

/**
 * Gets the sport name by its position on the array
 *
 * @param {string} pathname
 * @param {number} pos
 *
 * @returns {string} sport name
 *
 * @private
 */
var getSportAtPosition = function getSportAtPosition(pathname, pos) {

    var paths = pathname.split('/');

    return paths[pos];
};

/**
 * Calculates which properties have changed comparing the newly calculated arguments with the existing
 *
 * @param {Object} args Newly calculated arguments
 *
 * @returns {array} Array of changed props
 */
var calculatePropChanges = function calculatePropChanges(args) {

    return _.compact(_.map(args, hasChanged));
};

/**
 * Checks if a property has changed
 *
 * @param {string} value
 * @param {string} key
 *
 * @returns {string | Boolean}
 *
 * @private
 */
var hasChanged = function hasChanged(value, key) {

    return args[key] !== value ? key : false;
};

/**
 * Publishes the changes on the top priority level. If the sport changed only the sport topic is published. If
 * not then page is published. This is made to avoid concurrency issues with page/sport updates
 *
 * @param {Object} args The new arguments
 * @param {Array} changedProps Changed properties
 *
 * @returns {Boolean}
 *
 * @private
 */
var publishArgChanges = function publishArgChanges(args, changedProps) {

    return _.some(publishPriority, publish.bind(this, args, changedProps));
};

/**
 * Does the actual publishing of the message using PubSub
 *
 * @param {Object} args The new arguments
 * @param {Array} changedProps Changed properties
 * @param {string} prop The current property to be handled
 *
 * @returns {boolean}
 *
 * @private
 */
var publish = function publish(args, changedProps, prop) {

    if (changedProps.indexOf(prop) > -1) {

        pubSub.publish(`${MessageBusTopics.NAVIGATION_ARGS}.${prop}`, args);

        return true;
    }
};

/**
 * Public functions.
 *
 * @type {Object}
 */
var api = {

    /**
     * Sets the initial arguments from the sport and page passed from the BE
     *
     * @returns {void}
     *
     * @public
     */
    init: function init() {

        api.update();
    },

    /**
     * Gets the arguments from the passed URL
     *
     * @param {string} pathname  URL to get the arguments from.
     *
     * @returns {void}
     *
     * @public
     */
    update: function update(pathname) {

        if (!pathname) {

            pathname = Translator.translateUrl(window.location.pathname, 'to');
        }

        var localArgs = getArgsFromPath(pathname),
            changedProps = calculatePropChanges(localArgs);

        args = localArgs;

        publishArgChanges(localArgs, changedProps);
    },

    /**
     * Gets the current page arguments
     *
     * @returns {Object}
     *
     * @public
     */
    get: function () {

        return args;
    },
};

export default api;
